import { ME_DRAFT_OFFERS_LIMIT, meDraftOffers } from '@ooh/contracts';
import type { MeDraftOffersResponse } from '@ooh/contracts';
import { useApiQuery } from '../../../data/use-api-query';
import { apiQueryKey } from '../../../data/use-api-suspense-query';
import { useCurrentUser } from '../../../plugs/current-user';
import type { QuickAddOfferDraftOption } from '../quick-add-offer-picker';

export interface EditableDraftsQueryData {
  drafts: QuickAddOfferDraftOption[];
  selectedDraftId: string | null;
}

const DRAFTS_STALE_TIME_MS = 30_000;

function mapDraftToPickerOption(
  draft: MeDraftOffersResponse['drafts'][number],
): QuickAddOfferDraftOption {
  return {
    id: String(draft.offerId),
    title: draft.title,
    clientName: draft.clientName ?? '',
    unitCount: draft.unitCount,
    editable: true,
    lastUsed: draft.lastUsed,
  };
}

export function selectEditableDrafts(data: MeDraftOffersResponse): EditableDraftsQueryData {
  const drafts = data.drafts.map(mapDraftToPickerOption);
  return {
    drafts,
    selectedDraftId: drafts.find((draft) => draft.lastUsed)?.id ?? null,
  };
}

/**
 * `enabled: false` skips the fetch — locked-target quick-add call sites do not use the drafts
 * list, and web gates the whole feature on the `offer-drafts` capability.
 */
export function useEditableDraftsQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const { user } = useCurrentUser();
  const request = { limit: ME_DRAFT_OFFERS_LIMIT };
  const { data, isLoading } = useApiQuery(meDraftOffers.list, request, {
    enabled,
    staleTime: DRAFTS_STALE_TIME_MS,
    queryKey: [...apiQueryKey(meDraftOffers.list, request), user?.id ?? null],
    select: selectEditableDrafts,
  });
  return { data, isLoading };
}
